import {Component, EventEmitter, Input, Output} from '@angular/core';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {MediaObjectOptionsInterface} from '../../core/pipes/media-object.pipe';
import {NotificationInterface} from '../interfaces/notification.interface';
import {OwnerInvitationRestService} from '../../api/services/owner-invitation.rest.service';
import {OwnerInvitationApiModel} from '../../api/models/owner-invitation.api-model';
import {EventInvitationRestService} from '../../api/services/event-invitation.rest.service';
import {EventInvitationApiModel} from '../../api/models/event-invitation.api-model';
import {EventParticipationRequestRestService} from '../../api/services/event-participation-request.rest.service';

export type NotificationItemVariant = 'panel' | 'page';

/** Shared notification presentation and actions for the panel and full page. */
@Component({
  selector: 'app-notification-item',
  templateUrl: './notification-item.component.html',
  styleUrls: ['./notification-item.component.scss'],
})
export class NotificationItemComponent {
  @Input() public notification: NotificationInterface;
  @Input() public variant: NotificationItemVariant = 'panel';
  @Output() public readonly interacted = new EventEmitter<Event>();
  @Output() public readonly resolved = new EventEmitter<number>();
  public invitationActionPending = false;

  constructor(
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly ownerInvitationRS: OwnerInvitationRestService,
    private readonly eventInvitationRS: EventInvitationRestService,
    private readonly participationRequestRS: EventParticipationRequestRestService,
  ) {
  }

  public get thumbnail(): any {
    const subject: any = this.notification?.subject || {};
    return this.notification?.type === 'PROJECT'
      ? subject.thumbnail || subject.theme || this.notification?.fromUser?.avatar || null
      : this.notification?.fromUser?.avatar || null;
  }

  public get thumbnailOptions(): MediaObjectOptionsInterface {
    const subject: any = this.notification?.subject || {};
    return this.notification?.type === 'PROJECT'
      ? {
        defaultImageType: subject?.vehicleType?.includes?.('MOTORCYCLE') ? 'motorcycle' : 'car',
        formatType: 'thumb_sm',
      }
      : {defaultImageType: 'avatar', formatType: 'thumb_sm'};
  }

  public get message(): string {
    const notification = this.notification;
    const context: any = notification?.context || {};
    const subject: any = notification?.subject || {};
    const params = {
      mainOwnerUsername: notification?.fromUser?.username,
      user: notification?.fromUser?.username,
      vehicleName: context.vehicleName,
      advertisementTitle: context.advertisementTitle || subject.title,
      invitedByUsername: notification?.fromUser?.username,
      ...context,
    };
    const text = (key: string) => this.translate.instant(key, params);
    const project = this.projectLabel;
    const collaboration = context.collaborationTitle || (context.collaborationId ? '#' + context.collaborationId : '');

    switch (notification?.type) {
      case 'PROJECT':
        switch (notification.event) {
          case 'chat_project_mention':
            return `${text('shared.top_bar.notifications.mentioned_project_in_chat')} ${project} ${text('shared.top_bar.notifications.in_chat_on')} ${text('shared.top_bar.notifications.chat_link_label')}`;
          case 'collaboration_created':
          case 'collaboration_asset_approved':
          case 'collaboration_asset_rejected':
            return `${text('shared.top_bar.notifications.' + notification.event)} ${collaboration} ${text('shared.top_bar.notifications.in_project')} ${project}`;
          case 'instagram_sync_completed':
            return `${text('shared.top_bar.notifications.instagram_sync_completed')} ${project} ${text('shared.top_bar.notifications.instagram_sync_completed_guide')}`;
          case 'instagram_sync_failed':
            return `${text('shared.top_bar.notifications.instagram_sync_failed')} ${project}${context.errorMessage ? ': ' + context.errorMessage : ''}`;
          default:
            return `${text('shared.top_bar.notifications.follow_project')} ${project} ${text('shared.top_bar.notifications.from_your_garage')}`;
        }
      case 'OWNER':
        if (notification.event === 'project_owner_invitation') {
          return `${text('shared.top_bar.notifications.owner_invitation')} ${context.project || project}`;
        }
        if (notification.event === 'project_main_owner_change_invitation') {
          return `${text('shared.top_bar.notifications.main_owner_change')} ${context.project || project}`;
        }
        return `${text('shared.top_bar.notifications.owner_invitation_denied')} ${context.vehicleName || project}`;
      case 'EVENT':
        return `${text('shared.top_bar.notifications.' + notification.event)} ${context.event || ''}`.trim();
      case 'POST':
        if (notification.event === 'post_user_mention') {
          return `${text('shared.top_bar.notifications.mentioned_in_post')} #${subject.id || ''} ${context.projectName || ''}`.trim();
        }
        if (notification.event === 'post_project_mention') {
          return `${text('shared.top_bar.notifications.mentioned_project_in_post')} #${subject.id || ''} ${context.projectName || ''}`.trim();
        }
        return text('shared.top_bar.notifications.' + notification.event);
      case 'USER':
        if (notification.event === 'event_award_published') return text('event_awards.notification');
        if (notification.event === 'reputation_rank' || notification.event === 'reputation_badge') {
          const key = notification.event === 'reputation_rank' ? 'reputation.ranks.' + context.reputationId : 'reputation.badges.' + context.reputationId + '.name';
          return this.translate.instant('reputation.notifications.' + notification.event, {name: this.translate.instant(key)});
        }
        return text('shared.top_bar.notifications.follow_profile');
      case 'MARKETPLACE':
        return `${text('shared.top_bar.notifications.' + notification.event)} ${context.advertisementTitle || subject.title || ''}`.trim();
      case 'MEDIA_OBJECT':
        return text('shared.top_bar.notifications.media_object_ready');
      case 'DOCUMENT':
        return text('shared.top_bar.notifications.document_ready');
      default:
        return this.translate.instant('shared.mobile_navigation.notification_received', {username: notification?.fromUser?.username});
    }
  }

  public get projectLabel(): string {
    const subject: any = this.notification?.subject || {};
    const context: any = this.notification?.context || {};
    if (context.projectName) {
      return context.projectName;
    }
    if (subject?.alias && subject?.vehicle?.fullVehicleIdentity) {
      return `${subject.alias} (${subject.vehicle.fullVehicleIdentity})`;
    }
    return subject?.vehicle?.fullVehicleIdentity || subject?.alias || context.projectSlug
      || this.translate.instant('shared.top_bar.notifications.project_fallback');
  }

  public get isPendingOwnerInvitation(): boolean {
    return this.notification?.type === 'OWNER'
      && (this.notification.event === 'project_owner_invitation'
        || this.notification.event === 'project_main_owner_change_invitation')
      && !!this.notification.subject
      && !this.notification.subject.acceptedAt
      && !this.notification.subject.deniedAt;
  }

  public get isPendingEventInvitation(): boolean {
    return this.notification?.event === 'event_invitation'
      && !!this.notification.subject
      && !this.notification.subject.acceptedAt
      && !this.notification.subject.deniedAt;
  }

  public get isPendingParticipationRequest(): boolean {
    return this.notification?.event === 'event_participation_requested'
      && this.notification.subject?.status === 'PENDING';
  }

  public get confirmationToken(): string | null {
    return (this.notification?.context as any)?.token || null;
  }

  public get invitationId(): number | null {
    const value = (this.notification?.context as any)?.invitationId;
    return this.resolvePositiveId(value);
  }

  public get participationRequestId(): number | null {
    const contextId = (this.notification?.context as any)?.participationRequestId;
    return this.resolvePositiveId(contextId ?? this.notification?.subject?.id);
  }

  public confirmOwnerInvitation(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isPendingOwnerInvitation || !this.confirmationToken) {
      return;
    }

    const path = this.notification.event === 'project_main_owner_change_invitation'
      ? 'owner-change-confirmation'
      : 'invitation';
    this.router.navigate(['/', path, this.confirmationToken])
      .then(navigated => {
        if (navigated) {
          this.interacted.emit(event);
        }
      });
  }

  public denyOwnerInvitation(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isPendingOwnerInvitation || !this.invitationId || this.invitationActionPending) {
      return;
    }

    const invitation = new OwnerInvitationApiModel();
    invitation.id = this.invitationId;
    this.invitationActionPending = true;
    this.ownerInvitationRS.deny(invitation).subscribe({
      next: () => {
        this.resolved.emit(this.notification.id);
        this.interacted.emit(event);
      },
      error: () => {
        this.invitationActionPending = false;
      },
    });
  }

  public respondToEventInvitation(event: Event, response: 'accept' | 'deny'): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isPendingEventInvitation || !this.invitationId || this.invitationActionPending) {
      return;
    }

    const invitation = new EventInvitationApiModel();
    invitation.id = this.invitationId;
    this.invitationActionPending = true;
    this.eventInvitationRS[response](invitation).subscribe({
      next: () => {
        if (response === 'accept') {
          this.notification.subject.acceptedAt = new Date();
        } else {
          this.notification.subject.deniedAt = new Date();
        }
        this.invitationActionPending = false;
        this.interacted.emit(event);
      },
      error: () => {
        this.invitationActionPending = false;
      },
    });
  }

  public respondToParticipationRequest(event: Event, response: 'approve' | 'reject'): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isPendingParticipationRequest || !this.participationRequestId || this.invitationActionPending) {
      return;
    }

    this.invitationActionPending = true;
    this.participationRequestRS[response](this.participationRequestId).subscribe({
      next: () => {
        this.notification.subject.status = response === 'approve' ? 'APPROVED' : 'REJECTED';
        this.invitationActionPending = false;
        this.interacted.emit(event);
      },
      error: () => {
        this.invitationActionPending = false;
      },
    });
  }

  private resolvePositiveId(value: unknown): number | null {
    const id = Number(value);
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  public openAuthor(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const slug = this.notification?.fromUser?.slug;
    if (!slug) {
      return;
    }
    this.router.navigate(['/', slug]).then(() => this.interacted.emit(event));
  }

  public openNotification(event: Event): void {
    if (event.type === 'keydown' && event.target !== event.currentTarget) {
      return;
    }
    event.preventDefault();
    const route = this.getPrimaryRoute();
    if (!route) {
      this.interacted.emit(event);
      return;
    }
    this.router.navigate(route).then(() => this.interacted.emit(event));
  }

  private getPrimaryRoute(): string[] | null {
    const notification = this.notification;
    const context: any = notification?.context || {};
    const subject: any = notification?.subject || {};
    if (notification?.event === 'event_award_published' && this.resolvePositiveId(context.awardGrantId)) return ['/events/awards', String(context.awardGrantId)];
    if (notification?.event === 'reputation_rank' || notification?.event === 'reputation_badge') {
      const slug = subject.slug || notification.fromUser?.slug;
      return slug ? ['/', slug, 'reputation'] : null;
    }
    if (notification?.type === 'PROJECT') {
      const projectSlug = context.projectSlug || subject.slug;
      const ownerSlug = context.ownerSlug || subject?.user?.slug || notification?.fromUser?.slug;
      return projectSlug ? (ownerSlug ? ['/', ownerSlug, 'garage', projectSlug] : ['/garage', projectSlug]) : null;
    }
    if (notification?.type === 'OWNER' && context.projectSlug) {
      const ownerSlug = notification?.fromUser?.slug || context.mainOwnerSlug || context.oldMainOwnerSlug;
      return ownerSlug
        ? ['/', ownerSlug, 'garage', context.projectSlug]
        : ['/garage', context.projectSlug];
    }
    if (notification?.type === 'EVENT' && context.eventSlug) {
      return ['/events', context.eventSlug];
    }
    if (notification?.type === 'POST' && subject.id) {
      return ['/posts', String(subject.id)];
    }
    if (notification?.type === 'MARKETPLACE') {
      const advertisementSlug = context.advertisementSlug || subject.slug;
      return advertisementSlug ? ['/marketplace/offer', advertisementSlug] : null;
    }
    return null;
  }
}
